import { computed, unref, type MaybeRef } from 'vue'
import type { AxiosResponse } from 'axios'
import { createQuery } from '@/queries'
import { useTokenStore } from '@/features/auth/store'
import { getCapsulesPreview } from '@/features/capsules/api/capsules'
import type { Audience } from '@/features/recommendations/api/recommendations'

type HomeCapsulesPreviewResponse = Awaited<
  ReturnType<typeof getCapsulesPreview>
> extends AxiosResponse<infer T>
  ? T
  : never

export interface HomeCapsulesPreviewQueryInput {
  limit?: number
  audience: Audience
}

const DEFAULT_LIMIT = 10

export function useHomeCapsulesPreviewQuery(
  input: MaybeRef<HomeCapsulesPreviewQueryInput>,
) {
  const tokenStore = useTokenStore()

  // The same route answers a curated edition to a guest and a re-ranked or
  // personal one to a signed-in shopper: the cache must not hand one the other's.
  const viewer = computed(() => tokenStore.accessToken ?? 'guest')

  return createQuery<HomeCapsulesPreviewResponse>({
    key: computed(() => {
      const value = unref(input)
      return [
        'home',
        'capsules-preview',
        value.limit ?? DEFAULT_LIMIT,
        value.audience,
        viewer.value,
      ] as const
    }),
    fetcher: async () => {
      const value = unref(input)
      const response = await getCapsulesPreview(
        value.limit ?? DEFAULT_LIMIT,
        value.audience,
      )
      return response.data
    },
    staleTime: 10 * 60 * 1000,
  })
}
